import { getService } from './services';

interface DocumentWithCookies {
  cookie: string;
}

export type CookieOptions = {
  expires?: number;
  domain?: string;
  path?: string;
};

const getDomainInfo = (options: CookieOptions): string => {
  return options.domain ? `;domain=${options.domain}` : '';
};

const getPathInfo = (options: CookieOptions): string => {
  return `;path=${options.path ? options.path : '/'}`;
};

const shouldSilenceError = (error: unknown): boolean => {
  return error instanceof DOMException || error instanceof URIError;
};

export class CookieManager {
  private readonly document: DocumentWithCookies;

  public constructor(doc?: DocumentWithCookies) {
    this.document =
      doc || (typeof document === 'undefined' ? ({} as DocumentWithCookies) : document);
  }

  /**
   * Read a cookie value
   *
   * @param cookieName the name of the cookie
   * @returns the value of the cookie
   */
  public read(cookieName: string): string {
    // We need this try catch to avoid the sandbox error of embedded geniallys
    try {
      const name = `${cookieName}=`;
      const decodedCookie = decodeURIComponent(this.document.cookie);
      const ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i += 1) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
          return c.substring(name.length, c.length);
        }
      }
    } catch (error) {
      if (!shouldSilenceError(error)) {
        throw error;
      }
    }

    return '';
  }

  /**
   * Set a cookie value given a name
   *
   * @param cookieName the name of the cookie
   * @param value the new value of the cookie
   * @param options set cookie domain, path or expires time
   */
  public write(cookieName: string, value: string, options: CookieOptions): void {
    const pathInfo = getPathInfo(options);
    const domainInfo = getDomainInfo(options);
    let expiresInfo = '';
    if (options.expires) {
      const d = new Date();
      d.setTime(d.getTime() + options.expires);
      expiresInfo = `;expires=${d.toUTCString()}`;
    }

    this.document.cookie = `${cookieName}=${value}${expiresInfo}${domainInfo}${pathInfo}`;
  }

  /**
   * Delete a cookie value given a name
   *
   * @param cookieName the name of the cookie
   * @param options set cookie domain, path or expires time
   */
  public delete(cookieName: string, options: CookieOptions): void {
    const pathInfo = getPathInfo(options);
    const domainInfo = getDomainInfo(options);

    this.document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC${domainInfo}${pathInfo}`;
  }
}

export const getCookieManagerService = () => getService(CookieManager);
